import React, {useState} from 'react';
import {Stack, Box, Typography, Collapse} from '@mui/material';
import './QuerySearchCriteria.scss';
import {SessionSearch} from '../../types/SessionSearch';
import {Query} from '../../types/Query';
import ReadOnlyChip from '../query-list/query-panel/chip-panel/read-only-chip/ReadOnlyChip';
import {getOperatorByType} from '../query-list/operatorButtonUtils';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {t} from 'i18next';

export interface QuerySearchCriteriaProps {
  item: SessionSearch;
  onQueryModified?: (item: SessionSearch) => void;
}

const QuerySearchCriteria: React.FC<QuerySearchCriteriaProps> = ({item}) => {
  const [expanded, setExpanded] = useState(true);
  const queries = item.query ?? [];

  const getQueryTitle = (type: string): string => {
    switch (type) {
      case 'ContentAndMetadata':
        return 'Content and Metadata';
      case 'Conceptual':
        return 'Conceptual';
      case 'WorkProduct':
        return 'Work Product';
      case 'Analytics':
        return 'Analytics';
      default:
        return type;
    }
  };

  const handleExpandCollapseButtonClick = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleExpandCollapseButtonKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setExpanded((prev) => !prev);
    }
  };

  const renderQuerySection = (query: Query, index: number) => {
    const hasCriteria = query.value && query.value.length > 0;

    if (!hasCriteria) {
      return null;
    }

    // Check if there's a next query with criteria to determine if we show divider
    const hasNextQueryWithCriteria = queries
      .slice(index + 1)
      .some((q) => q.value && q.value.length > 0);

    // Get the operator for this query (used for the divider with next query)
    const queryOperator = getOperatorByType(query.operator) || 'AND';

    return (
      <Box key={query.type} className="query-section">
        <Typography
          className="typo-text subhead5_size query-section-title"
          data-testid={`query-search-criteria-query-title`}
          tabIndex={0}
        >
          {getQueryTitle(query.type)}
        </Typography>
        <Box className="query-criteria-chips">
          {query.value.map((criteria) => (
            <ReadOnlyChip key={criteria.id} criteria={criteria} />
          ))}
        </Box>
        {hasNextQueryWithCriteria && (
          <Box className="query-section-divider">
            <Typography
              className="typo-text label3_size query-section-operator"
              data-testid={`query-search-criteria-query-operator`}
              tabIndex={0}
            >
              {queryOperator}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className={'results-panel'}>
      {/* Panel Header */}
      <Stack
        direction={'row'}
        spacing={1}
        className={`results-panel-header ${expanded ? 'expanded' : 'collapsed'}`}
      >
        <Box className="results-panel-header_title">
          <Typography
            className="typo-text subhead4_size"
            data-testid={`query-search-criteria-text`}
            tabIndex={0}
          >
            {'Search Query'}
          </Typography>
        </Box>
        <Box className="results-panel-header_actions">
          <ArrIconButton
            icon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={handleExpandCollapseButtonClick}
            onKeyDown={handleExpandCollapseButtonKeyDown}
            ariaLabel={
              expanded
                ? t('sessionSearch.queryList.collapse')
                : t('sessionSearch.queryList.expand')
            }
            tooltipContent={
              expanded
                ? t('sessionSearch.queryList.collapse')
                : t('sessionSearch.queryList.expand')
            }
            size="small"
            className="icon-expand_collapse"
            testId="results-panel-expand_collapse"
          />
        </Box>
      </Stack>
      {/* Panel Content */}
      <Collapse in={expanded}>
        <Box className="results-panel-content">
          {queries.map((query, index) => renderQuerySection(query, index))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default QuerySearchCriteria;
